"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useTranslations } from "next-intl";
import Cropper from "react-easy-crop";

import ChevronLeftIcon from "@/icons/ui/media/chevron-left.svg";
import ChevronRightIcon from "@/icons/ui/media/chevron-right.svg";
import CropToolIcon from "@/icons/ui/media/crop-tool.svg";
import Modal from "@/ui/Common/Modal";

import styles from "./styles.module.scss";

// Import icon components

// Utility function to fetch image through proxy to avoid CORS issues
const fetchImageThroughProxy = async (imageUrl: string): Promise<Blob> => {
  // Check if URL needs proxying (Google Storage URLs)
  if (imageUrl.startsWith("https://storage.googleapis.com/avezor-images/")) {
    const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
    const response = await fetch(proxyUrl);
    if (!response.ok) {
      throw new Error(`Proxy request failed: ${response.status}`);
    }
    return await response.blob();
  }

  // For blob URLs that might be revoked, wrap in try/catch
  if (imageUrl.startsWith("blob:")) {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Blob fetch failed: ${response.status}`);
      }
      return await response.blob();
    } catch (error) {
      throw new Error(`Blob URL is no longer valid or accessible: ${error}`);
    }
  }

  // For other URLs (data:), fetch directly
  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error(`Direct fetch failed: ${response.status}`);
  }
  return await response.blob();
};

// Crop image function
const cropImage = (
  file: File,
  cropArea: { x: number; y: number; width: number; height: number },
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Cannot get canvas context"));
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;

      ctx.drawImage(
        img,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        cropArea.width,
        cropArea.height,
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Cannot create blob from canvas"));
            return;
          }
          const croppedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(croppedFile);
        },
        file.type,
        0.95,
      );
    };

    img.onerror = () => reject(new Error("Error loading image"));
    img.src = URL.createObjectURL(file);
  });
};

export interface CropModalProps {
  cropFile: any;
  setCropFile: (file: any) => void;
  cropIndex?: number;
  handleAddPhoto: (files: File[], index?: number) => void;
  displayPhotos?: any[];
  onChange?: (photos: any[]) => void;
  setCropPhotoIndex?: (index: number | undefined) => void;
  isOpen: boolean;
  onApply?: (cropArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => Promise<void>;
}

export const CropModal: React.FC<CropModalProps> = ({
  cropFile,
  setCropFile,
  cropIndex,
  handleAddPhoto,
  displayPhotos = [],
  onChange,
  setCropPhotoIndex,
  isOpen,
  onApply,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageSize, setImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [displayImageUrl, setDisplayImageUrl] = useState<string>("");
  const cropperRef = useRef(null);

  const [aspect, setAspect] = useState({ x: 4, y: 3 });

  useEffect(() => {
    setAspect({ x: 4, y: 3 });
    setZoom(1);
  }, [cropFile]);

  const t = useTranslations();

  // Handle image URL for display in Cropper
  useEffect(() => {
    if (!cropFile?.preview) {
      setDisplayImageUrl("");
      return;
    }

    // For Google Storage URLs, use proxy to avoid CORS issues
    if (
      cropFile.preview.startsWith(
        "https://storage.googleapis.com/avezor-images/",
      )
    ) {
      const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(cropFile.preview)}`;
      setDisplayImageUrl(proxyUrl);
    } else {
      // For blob URLs, data URLs, and other URLs, use directly
      setDisplayImageUrl(cropFile.preview);
    }
  }, [cropFile]);

  useEffect(() => {
    if (displayImageUrl) {
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.crossOrigin = "anonymous";
      img.src = displayImageUrl;
    }
  }, [displayImageUrl]);

  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const handleCrop = async () => {
    if (!cropFile || !croppedAreaPixels) {
      return;
    }

    // If we have onApply callback, use the parent's crop logic
    if (onApply) {
      try {
        await onApply(croppedAreaPixels);
        setCropFile(null);
        if (setCropPhotoIndex) {
          setCropPhotoIndex(undefined);
        }
      } catch (error) {
        console.error("Error in onApply:", error);
      }
      return;
    }

    // Fallback to internal crop logic if no onApply provided
    try {
      // If cropFile is a File object, use it directly
      // Otherwise, if we have fileData (serialized), create File from it
      let fileToProcess: File;

      if (cropFile instanceof File) {
        fileToProcess = cropFile;
      } else if (cropFile.file instanceof File) {
        fileToProcess = cropFile.file;
      } else if (cropFile.fileData) {
        // Create File from base64 string
        const binaryString = atob(cropFile.fileData.split(",")[1]);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: cropFile.fileType || "image/jpeg",
        });
        fileToProcess = new File([blob], cropFile.fileName || "image.jpg", {
          type: cropFile.fileType || "image/jpeg",
          lastModified: Date.now(),
        });
      } else if (cropFile.preview) {
        // If we only have preview URL, get data from it
        if (cropFile.preview.startsWith("data:")) {
          // Data URL - extract data directly
          const response = await fetch(cropFile.preview);
          const blob = await response.blob();
          fileToProcess = new File([blob], cropFile.fileName || "image.jpg", {
            type: blob.type || "image/jpeg",
            lastModified: Date.now(),
          });
        } else {
          // Server URL, blob URL or any other URL
          try {
            // Check URL type and use appropriate method
            if (cropFile.preview.startsWith("blob:")) {
              // For blob URLs try direct fetch
              try {
                const response = await fetch(cropFile.preview);
                if (!response.ok) {
                  throw new Error(`Blob fetch failed: ${response.status}`);
                }
                const blob = await response.blob();
                fileToProcess = new File(
                  [blob],
                  cropFile.fileName || "image.jpg",
                  {
                    type: blob.type || cropFile.fileType || "image/jpeg",
                    lastModified: Date.now(),
                  },
                );
              } catch (blobError) {
                // If blob URL is not accessible, try to use file if available
                if (cropFile.file) {
                  fileToProcess = cropFile.file;
                } else {
                  throw new Error(
                    "Blob URL is no longer accessible and no file available",
                  );
                }
              }
            } else {
              // For server URLs use proxy
              const blob = await fetchImageThroughProxy(cropFile.preview);
              fileToProcess = new File(
                [blob],
                cropFile.fileName || "image.jpg",
                {
                  type: blob.type || cropFile.fileType || "image/jpeg",
                  lastModified: Date.now(),
                },
              );
            }
          } catch (fetchError) {
            console.error("Error fetching image for crop:", fetchError);
            throw new Error("Cannot process image from preview URL");
          }
        }
      } else {
        // If none of the formats are suitable
        throw new Error("Invalid cropFile format - no usable image data found");
      }

      const croppedImage = await cropImage(fileToProcess, croppedAreaPixels);
      setCropFile(null);
      if (setCropPhotoIndex) {
        setCropPhotoIndex(undefined); // Reset index after cropping
      }

      // If cropIndex is provided, we're editing an existing photo - replace it
      if (
        cropIndex !== undefined &&
        cropIndex !== -1 &&
        onChange &&
        displayPhotos
      ) {
        // Find the photo being edited and replace it
        const existingPhotos = displayPhotos || [];
        const updatedPhotos = existingPhotos.map((photo, index) => {
          if (index === cropIndex) {
            // Create updated photo with cropped image
            const preview = URL.createObjectURL(croppedImage);
            // Free old URL if it was a blob
            if (photo.preview.startsWith("blob:")) {
              URL.revokeObjectURL(photo.preview);
            }

            return {
              ...photo,
              file: croppedImage,
              fileName: croppedImage.name,
              fileType: croppedImage.type,
              fileSize: croppedImage.size,
              preview,
              blobPreview: preview,
              // Reset upload status for re-upload if needed
              uploadStatus: "pending" as const,
              uploadProgress: 0,
              error: undefined,
            };
          }
          return photo;
        });
        onChange(updatedPhotos);
      } else {
        // Adding new photo
        handleAddPhoto([croppedImage]);
      }
    } catch (e) {
      console.error(t("ErrorCroppingImage"), e);
    }
  };

  const handleCancel = () => {
    setCropFile(null);
    if (setCropPhotoIndex) {
      setCropPhotoIndex(undefined); // Reset index
    }
  };

  const moveAreaLeft = () => {
    // setCrop((prev) => ({ ...prev, x: prev.x - 10 }));
    setAspect((prev) => ({ ...prev, x: prev.x - 0.2 }));
  };

  const moveAreaRight = () => {
    // setCrop((prev) => ({ ...prev, x: prev.x + 10 }));
    setAspect((prev) => ({ ...prev, x: prev.x + 0.2 }));
  };

  const moveAreaUp = () => {
    // setCrop((prev) => ({ ...prev, y: prev.y - 10 }));
    setAspect((prev) => ({ ...prev, y: prev.y + 0.2 }));
  };

  const moveAreaDown = () => {
    // setCrop((prev) => ({ ...prev, y: prev.y + 10 }));
    setAspect((prev) => ({ ...prev, y: prev.y - 0.2 }));
  };

  // Get current image size and recommended size
  const currentSize = imageSize
    ? `${imageSize.width}x${imageSize.height}`
    : "0x0";
  const recommendedSize = "720x540";

  if (!isOpen) {
    return null;
  }

  return (
    <Modal onClose={handleCancel}>
      <div className={styles.cropContainer}>
        <div className={styles.cropHeader}>
          <CropToolIcon
            width={22}
            height={17}
            className={styles.cropIcon}
          />
          <h3 className={styles.cropTitle}>{t("CropImage")}</h3>
        </div>

        <div className={styles.cropInfoContainer}>
          <div className={styles.cropInfoText}>{t("CropImageInfo")}</div>
          <div className={styles.cropSizeInfo}>
            {t("CropImageSizeInfo", {
              currentSize,
              recommendedSize,
            })}
          </div>
        </div>

        <div className={styles.cropperWrapper}>
          {cropFile && (
            <Cropper
              ref={cropperRef}
              image={displayImageUrl}
              crop={crop}
              zoom={zoom}
              restrictPosition={true}
              aspect={aspect.x / aspect.y}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              objectFit="contain"
            />
          )}
        </div>

        <div className={styles.cropControls}>
          <button
            type="button"
            className={styles.cropControlButton}
            onClick={moveAreaLeft}
            title={t("MoveLeft")}
            aria-label={t("MoveLeft")}
          >
            <ChevronLeftIcon
              width={24}
              height={24}
              className={styles["icon-gray"]}
            />
            <ChevronLeftIcon
              width={24}
              height={24}
              className={styles["icon-red"]}
            />
            <ChevronLeftIcon
              width={24}
              height={24}
              className={styles["icon-white"]}
            />
          </button>

          <button
            type="button"
            className={styles.cropControlButton}
            onClick={moveAreaRight}
            title={t("MoveRight")}
            aria-label={t("MoveRight")}
          >
            <ChevronRightIcon
              width={24}
              height={24}
              className={styles["icon-gray"]}
            />
            <ChevronRightIcon
              width={24}
              height={24}
              className={styles["icon-red"]}
            />
            <ChevronRightIcon
              width={24}
              height={24}
              className={styles["icon-white"]}
            />
          </button>

          {/* <button
            type="button"
            className={styles.cropControlButton}
            onClick={moveAreaUp}
            title={t("MoveUp")}
            aria-label={t("MoveUp")}
          >
            <ChevronUpIcon
              width={24}
              height={24}
              className={styles["icon-gray"]}
            />
            <ChevronUpIcon
              width={24}
              height={24}
              className={styles["icon-red"]}
            />
            <ChevronUpIcon
              width={24}
              height={24}
              className={styles["icon-white"]}
            />
          </button>

          <button
            type="button"
            className={styles.cropControlButton}
            onClick={moveAreaDown}
            title={t("MoveDown")}
            aria-label={t("MoveDown")}
          >
            <ChevronDownIcon
              width={24}
              height={24}
              className={styles["icon-gray"]}
            />
            <ChevronDownIcon
              width={24}
              height={24}
              className={styles["icon-red"]}
            />
            <ChevronDownIcon
              width={24}
              height={24}
              className={styles["icon-white"]}
            />
          </button> */}
        </div>

        <div className={styles.cropActions}>
          <button
            type="button"
            className={`${styles.cropButton} ${styles.cancelButton}`}
            onClick={handleCancel}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={`${styles.cropButton} ${styles.applyButton}`}
            onClick={handleCrop}
          >
            {t("Apply")}
          </button>
        </div>
      </div>
    </Modal>
  );
};
